import React from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { isInIframe, usePenpal } from './hooks/usePenpal';
import { useUserHasCloudEnabled } from './store/userStore';
import { useUIStore } from './store/uiStore';

import LoadingOverlay from './components/layout/LoadingOverlay';
import AddGuidePage from './pages/AddGuidePage';
import EditGuidePage from './pages/EditGuidePage';
import DashboardPage from './pages/DashboardPage';
import PortalSettingsPage from './pages/PortalSettingsPage';
import ArticlesPage from './pages/ArticlesPage';
import EditArticlePage from './pages/EditArticlePage';
import GuidesPage from './pages/GuidesPage';
import HelpCentersPage from './pages/HelpCentersPage';
import EditHelpCenterStructurePage from './pages/EditHelpCenterStructurePage';
import ReportsPage from './pages/ReportsPage';
import LicenseManagerPage from './pages/LicenseManagerPage';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { NavigationConfirmModal } from './components/modals/NavigationConfirmModal';
import { NavigationBlocker } from './components/NavigationBlocker';
import { AuthGuard } from './components/AuthGuard';
import { BASE_PATH } from './config/basePath';

const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  usePenpal(); // Initialize Penpal connection
  
  return (
    <div className="relative bg-sidebar min-h-screen">
      <Toaster 
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#363636',
            color: '#fff',
          },
        }}
      />
      
      <NavigationBlocker />
      
      <main className="flex-1">
        {children}
      </main>
      
      {/* Global modals */}
      <NavigationConfirmModal />
    </div>
  );
};

const Root: React.FC = () => {
  const userHasCloudEnabled = useUserHasCloudEnabled();
  const { isLoading } = useUIStore();
  
  // Show loading overlay while initializing
  if (isLoading) {
    return <LoadingOverlay message="Initializing..." />;
  }

  // Show message if cloud is disabled
  if (!userHasCloudEnabled && isInIframe()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sidebar">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Cloud Disabled</h1>
          <p className="text-gray-600">Your administrator has disabled the Folge Cloud funcionality</p>
        </div>
      </div>
    );
  }

  return (
    <AuthGuard>
      <AppLayout>
        <Outlet />
      </AppLayout>
    </AuthGuard>
  );
};

const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/reset-password',
    element: <ResetPasswordPage />,
  },
  {
    path: '/',
    element: <Root />,
    children: [
      {
        index: true,
        element: <DashboardPage />,
      },
      {
        path: 'guides',
        element: <GuidesPage />,
      },
      {
        path: 'guides/new',
        element: <AddGuidePage />,
      },
      {
        path: 'guides/:id/edit',
        element: <EditGuidePage />,
      },
      {
        path: 'help-centers',
        element: <HelpCentersPage />,
      },
      {
        path: 'help-centers/:id/structure',
        element: <EditHelpCenterStructurePage />,
      },
      {
        path: 'reports',
        element: <ReportsPage />,
      },
      {
        path: 'license-manager',
        element: <LicenseManagerPage />,
      },
      {
        path: 'articles',
        element: <ArticlesPage />,
      },
      {
        path: 'help-centers/:helpCenterId/items/:itemId/edit',
        element: <EditArticlePage />,
      },
      {
        path: 'articles/:articleId/edit',
        element: <Navigate to="/help-centers" replace />, // Legacy route - redirect
      },
      {
        path: 'settings/guides-appearance',
        element: <PortalSettingsPage />,
      },
      {
        path: 'settings/portal',
        element: <Navigate to="/settings/guides-appearance" replace />, // Legacy route - redirect
      },
      {
        path: '*',
        element: <Navigate to="/" replace />,
      },
    ],
  },
], {
  basename: BASE_PATH
});

const App: React.FC = () => {
  return <RouterProvider router={router} />;
};

export default App;
